/**
 * Deck Template v2 (`content.schema === 'quill.deck-template'`) in the editor: reading, geometry
 * and the pure edits the DeckTemplateEditor makes.
 *
 * The content contract (shapes, fields, ids, limits, normalisation) lives in
 * src/utils/deckFields.js, the browser twin of cloud-run-worker/deckFields.js. Templates are
 * normalised with it when loaded and again when saved, so the worker and the editor always agree
 * and a template saves and reloads identically. The edits here are small immutable updates (so
 * undo keeps cheap snapshots and typing is never re-normalised mid-word); they keep the parts the
 * normaliser derives (field `id` and `slideKey`, `shape.fieldId`) in step.
 */
import { TEMPLATE_VARIABLES } from '../../utils/templateFields';
import {
  DECK_SCHEMA,
  DECK_VARIABLES,
  MAX_BRIEF_PART_CHARS,
  MAX_INSTRUCTION_CHARS,
  MAX_PURPOSE_CHARS,
  MAX_REASON_CHARS,
  SLIDE_ROLES as DECK_SLIDE_ROLES,
  deckFieldId as contractFieldId,
  defaultFitFor,
  limitsFromCapacity,
  normalizeDeckTemplate,
} from '../../utils/deckFields';

/** Schema marker of a v2 deck template. */
export const DECK_TEMPLATE_SCHEMA = DECK_SCHEMA;

/** EMU per inch and per point. */
export const EMU_PER_INCH = 914400;
export const EMU_PER_POINT = 12700;

/** Default slide size (16:9 widescreen, EMU) when the template does not say. */
export const DEFAULT_SLIDE_SIZE = Object.freeze({ cx: 12192000, cy: 6858000 });

/** What a shape does at generation time. */
export const SHAPE_ACTIONS = Object.freeze([
  { value: 'ai_field', label: 'AI field', help: 'Quill writes this box from the project evidence.' },
  { value: 'optional', label: 'Optional AI field', help: 'Written when there is evidence for it, removed when empty.' },
  { value: 'variable', label: 'Code variable', help: 'Filled by Quill from project data, never by the AI.' },
  { value: 'fixed', label: 'Fixed', help: 'Kept exactly as in the master.' },
  { value: 'clear', label: 'Clear', help: 'Emptied when the deck is built.' },
]);

/** Actions that carry a field spec. */
export const FIELD_ACTIONS = Object.freeze(['ai_field', 'optional']);

/** Field output kinds the editor offers (image and chart data come in a later wave). */
export const FIELD_KINDS = Object.freeze([
  { value: 'text', label: 'Text' },
  { value: 'list', label: 'Bullets' },
  { value: 'rich', label: 'Paragraphs' },
  { value: 'table_rows', label: 'Table rows' },
]);

/** Evidence sources. */
export const FIELD_SOURCES = Object.freeze([
  { value: 'any', label: 'Any evidence' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'sessions', label: 'Sessions' },
  { value: 'project', label: 'Project info' },
]);

/** What happens when generated text does not fit the box. */
export const FIT_POLICIES = Object.freeze([
  { value: 'shrink_then_split', label: 'Shrink, then continue on a new slide' },
  { value: 'shrink', label: 'Shrink text to fit' },
  { value: 'shorten_only', label: 'Shorten the text only' },
]);

/** Slide include rules. */
export const INCLUDE_RULES = Object.freeze([
  { value: 'always', label: 'Always include' },
  { value: 'optional', label: 'Include when relevant' },
  { value: 'repeat', label: 'Repeat for each item' },
  { value: 'exclude', label: 'Leave out' },
]);

/** What a repeating slide repeats over. */
export const REPEAT_UNITS = Object.freeze([
  { value: 'requirement', label: 'Requirement' },
  { value: 'epic', label: 'Epic' },
  { value: 'session', label: 'Session' },
  { value: 'ai', label: 'Decided by the AI' },
]);

/** Slide roles (the contract's list). */
export const SLIDE_ROLES = DECK_SLIDE_ROLES;

/** Speaker notes handling. */
export const NOTES_MODES = Object.freeze([
  { value: 'keep', label: 'Keep the master notes' },
  { value: 'ai', label: 'Write notes with AI' },
  { value: 'clear', label: 'No notes' },
]);

/** Code variables offered by the picker (the deck contract's names, with the Word labels). */
export const CODE_VARIABLES = Object.freeze(DECK_VARIABLES.map((name) => ({
  name,
  label: TEMPLATE_VARIABLES.find((v) => v.name === name)?.label || name,
})));

/** Longest prompt kept. */
export const MAX_INSTRUCTION_LENGTH = MAX_INSTRUCTION_CHARS;

/** Limits the editor offers. */
export const LIMIT_KEYS = Object.freeze(['maxChars', 'maxLines', 'maxBullets', 'maxCharsPerBullet', 'maxRows']);

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const finite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Parse and normalise stored template content as a deck template.
 * @param {string|object|null|undefined} content - `templates.content` (JSON string) or an object.
 * @returns {object|null} The normalised template, or null when it is not a v2 deck template.
 */
export function parseDeckTemplate(content) {
  return normalizeDeckTemplate(content);
}

/**
 * Whether stored template content is a v2 deck template.
 * @param {string|object|null|undefined} content - Template content.
 * @returns {boolean} True for `quill.deck-template` content.
 */
export function isDeckTemplateContent(content) {
  return parseDeckTemplate(content) !== null;
}

/**
 * Template content to save (normalised, so it reloads identically).
 * @param {object} model - Deck template.
 * @returns {string} JSON.
 */
export function serializeDeckTemplate(model) {
  return JSON.stringify(normalizeDeckTemplate(model) || model);
}

/**
 * Slide size in EMU.
 * @param {object} model - Deck template.
 * @returns {{cx: number, cy: number}} Size.
 */
export function slideSizeOf(model) {
  const size = model?.slideSize;
  const cx = finite(size?.cx) ?? finite(size?.width);
  const cy = finite(size?.cy) ?? finite(size?.height);
  if (cx > 0 && cy > 0) return { cx, cy };
  return { ...DEFAULT_SLIDE_SIZE };
}

/**
 * A shape's box in EMU, from any of the forms the manifest may use.
 * @param {object} shape - Shape.
 * @returns {{x: number, y: number, cx: number, cy: number}|null} Box, or null without geometry.
 */
export function bboxOf(shape) {
  const box = shape?.bbox ?? shape?.geometry ?? null;
  if (!box) return null;
  let x;
  let y;
  let cx;
  let cy;
  if (Array.isArray(box)) {
    [x, y, cx, cy] = box;
  } else if (isObject(box.off) || isObject(box.ext)) {
    x = box.off?.x;
    y = box.off?.y;
    cx = box.ext?.cx;
    cy = box.ext?.cy;
  } else {
    x = box.x;
    y = box.y;
    cx = box.cx ?? box.w ?? box.width;
    cy = box.cy ?? box.h ?? box.height;
  }
  if ([x, y, cx, cy].some((n) => finite(n) === null)) return null;
  if (cx <= 0 || cy <= 0) return null;
  // The frame is unrotated; rot (degrees clockwise about the centre) says how it is drawn.
  const rot = finite(box.rot);
  return rot ? { x, y, cx, cy, rot } : { x, y, cx, cy };
}

/**
 * A box as percentages of the slide (for absolutely positioned outlines).
 * @param {{x: number, y: number, cx: number, cy: number}} box - Box in EMU.
 * @param {{cx: number, cy: number}} size - Slide size in EMU.
 * @returns {{left: number, top: number, width: number, height: number}} Percentages.
 */
export function toPercentRect(box, size) {
  return {
    left: (box.x / size.cx) * 100,
    top: (box.y / size.cy) * 100,
    width: (box.cx / size.cx) * 100,
    height: (box.cy / size.cy) * 100,
  };
}

/**
 * Inches, rounded to 2 decimals.
 * @param {number} emu - Length in EMU.
 * @returns {number} Inches.
 */
export function emuToInches(emu) {
  return Math.round((emu / EMU_PER_INCH) * 100) / 100;
}

/**
 * Slides in order, with their 0-based position and 1-based render page (hidden slides are left
 * out of the rendered PDF, so they have no page).
 * @param {object} model - Deck template.
 * @returns {Array<object>} Slides: the stored slide plus `index`, `number` and `renderPage`.
 */
export function slidesOf(model) {
  const dropped = droppedReasons(model);
  let page = 0;
  return (model?.slides || []).filter((slide) => slide && slide.key).map((slide, index) => {
    const hidden = slide.hidden === true;
    if (!hidden) page += 1;
    const excludedReason = dropped.get(slide.key);
    const excluded = slide.include === 'exclude' || excludedReason !== undefined;
    return {
      ...slide,
      index,
      number: index + 1,
      renderPage: hidden ? null : page,
      excluded,
      excludeReason: slide.excludeReason || excludedReason || '',
    };
  });
}

/**
 * Drop reasons from `droppedSlides` (an import may list dropped slides there).
 * @param {object} model - Deck template.
 * @returns {Map<string, string>} Slide key to reason.
 */
function droppedReasons(model) {
  const map = new Map();
  (Array.isArray(model?.droppedSlides) ? model.droppedSlides : []).forEach((item) => {
    const key = item?.slideKey || item?.key;
    if (key) map.set(key, String(item.reason || ''));
  });
  return map;
}

/**
 * Shapes per slide key, in manifest order.
 * @param {object} model - Deck template.
 * @returns {Map<string, Array<object>>} Slide key to shapes.
 */
export function shapesBySlide(model) {
  const map = new Map();
  (model?.shapes || []).forEach((shape) => {
    if (!shape || !shape.key) return;
    const slideKey = shape.slideKey || String(shape.key).split('/')[0];
    if (!map.has(slideKey)) map.set(slideKey, []);
    map.get(slideKey).push(shape);
  });
  return map;
}

/**
 * Fields by the shape they belong to (with their id).
 * @param {object} model - Deck template.
 * @returns {Map<string, object>} Shape key to `{id, ...field}`.
 */
export function fieldsByShape(model) {
  const map = new Map();
  Object.entries(model?.fields || {}).forEach(([id, field]) => {
    if (field && field.shapeKey && !map.has(field.shapeKey)) map.set(field.shapeKey, { ...field, id });
  });
  return map;
}

/**
 * The action a shape has (a shape with a field but no action counts as an AI field).
 * @param {object} shape - Shape.
 * @param {object|undefined} field - Its field.
 * @returns {string} Action.
 */
export function actionOf(shape, field) {
  if (shape?.action && SHAPE_ACTIONS.some((a) => a.value === shape.action)) return shape.action;
  return field ? 'ai_field' : 'fixed';
}

/**
 * How a shape is drawn on the canvas.
 * @param {object} shape - Shape.
 * @param {object|undefined} field - Its field.
 * @returns {'ai'|'suggested'|'variable'|'fixed'|'clear'} Outline tone.
 */
export function outlineTone(shape, field) {
  const action = actionOf(shape, field);
  if (FIELD_ACTIONS.includes(action)) return field && field.state !== 'accepted' ? 'suggested' : 'ai';
  if (action === 'variable') return 'variable';
  if (action === 'clear') return 'clear';
  return 'fixed';
}

/**
 * Label of an action value.
 * @param {string} action - Action.
 * @returns {string} Label.
 */
export function actionLabel(action) {
  return SHAPE_ACTIONS.find((a) => a.value === action)?.label || 'Fixed';
}

/**
 * Short human name of a shape for lists and screen readers.
 * @param {object} shape - Shape.
 * @param {number} ordinal - 1-based position on the slide.
 * @returns {string} Name.
 */
export function shapeDisplayName(shape, ordinal) {
  const name = typeof shape?.name === 'string' ? shape.name.trim() : '';
  if (name) return name;
  if (shape?.phType) return `${shape.phType} placeholder`;
  return `Shape ${ordinal}`;
}

/**
 * Suggested (not yet accepted) fields, optionally on one slide.
 * @param {object} model - Deck template.
 * @param {string} [slideKey] - Limit to this slide.
 * @returns {string[]} Field ids.
 */
export function suggestedFieldIds(model, slideKey) {
  const shapes = new Map((model?.shapes || []).filter((s) => s && s.key).map((s) => [s.key, s]));
  // Slides left out of generated decks have nothing to review (restoring one brings them back).
  const excluded = excludedSlideKeys(model);
  return Object.entries(model?.fields || {})
    .filter(([, field]) => {
      if (!field || field.state === 'accepted') return false;
      const shape = shapes.get(field.shapeKey);
      if (!shape || !FIELD_ACTIONS.includes(actionOf(shape, field))) return false;
      const shapeSlide = shape.slideKey || String(shape.key).split('/')[0];
      if (excluded.has(shapeSlide)) return false;
      return !slideKey || shapeSlide === slideKey;
    })
    .map(([id]) => id);
}

/**
 * Keys of the slides left out of generated decks.
 * @param {object} model - Deck template.
 * @returns {Set<string>} Slide keys.
 */
export function excludedSlideKeys(model) {
  return new Set(slidesOf(model).filter((slide) => slide.excluded).map((slide) => slide.key));
}

/**
 * Field counts for the header and filmstrip.
 * @param {object} model - Deck template.
 * @returns {{fields: number, suggested: number, variables: number, fixed: number}} Counts.
 */
export function deckCounts(model) {
  const byShape = fieldsByShape(model);
  const excluded = excludedSlideKeys(model);
  const counts = { fields: 0, suggested: 0, variables: 0, fixed: 0 };
  (model?.shapes || []).forEach((shape) => {
    if (!shape || !shape.key) return;
    const field = byShape.get(shape.key);
    const tone = outlineTone(shape, field);
    if (tone === 'ai' || tone === 'suggested') counts.fields += 1;
    // Only slides that are generated count as suggestions to review.
    if (tone === 'suggested' && !excluded.has(shape.slideKey || String(shape.key).split('/')[0])) counts.suggested += 1;
    if (tone === 'variable') counts.variables += 1;
    if (tone === 'fixed' || tone === 'clear') counts.fixed += 1;
  });
  return counts;
}

/**
 * Limits the box can hold (from the manifest's capacity).
 * @param {object} shape - Shape.
 * @returns {{maxChars?: number, maxBullets?: number, maxCharsPerBullet?: number, maxRows?: number, approximate?: boolean}} Capacity.
 */
export function capacityOf(shape) {
  const capacity = isObject(shape?.capacity) ? shape.capacity : {};
  const out = {};
  LIMIT_KEYS.forEach((key) => {
    const n = finite(capacity[key]);
    if (n !== null && n > 0) out[key] = Math.floor(n);
  });
  if (capacity.approximate === true) out.approximate = true;
  return out;
}

/**
 * Limits set above what the box can hold.
 * @param {object} field - Field.
 * @param {object} shape - Its shape.
 * @returns {string[]} Limit keys that exceed capacity.
 */
export function limitsOverCapacity(field, shape) {
  const capacity = capacityOf(shape);
  const limits = isObject(field?.limits) ? field.limits : {};
  return LIMIT_KEYS.filter((key) => finite(limits[key]) !== null && capacity[key] !== undefined && limits[key] > capacity[key]);
}

/**
 * Field id for a shape from the contract (`f_` + FNV-1a of `deck:<shapeKey>`), retried with
 * `#2`, `#3`, ... while the id belongs to a field of another shape.
 * @param {string} shapeKey - Shape key `sld{sldId}/sp{cNvPr id}`.
 * @param {object} [fields] - Existing fields.
 * @returns {string} Field id.
 */
export function deckFieldId(shapeKey, fields = {}) {
  let attempt = 1;
  for (;;) {
    const id = contractFieldId(shapeKey, attempt);
    const taken = fields[id];
    if (!taken || taken.shapeKey === shapeKey) return id;
    attempt += 1;
  }
}

/**
 * Default field kind for a shape.
 * @param {object} shape - Shape.
 * @returns {string} Kind.
 */
function defaultKindFor(shape) {
  if (shape?.kind === 'table') return 'table_rows';
  const paragraphs = finite(shape?.paragraphs);
  const levels = Array.isArray(shape?.levels) ? shape.levels : [];
  if (shape?.phType === 'body' || (paragraphs !== null && paragraphs > 1) || levels.length > 1) return 'list';
  return 'text';
}

/**
 * Replace one shape by key.
 * @param {object} model - Deck template.
 * @param {string} shapeKey - Shape key.
 * @param {function(object): object} update - New shape from the old one.
 * @returns {object} New model.
 */
function mapShape(model, shapeKey, update) {
  return { ...model, shapes: model.shapes.map((shape) => (shape && shape.key === shapeKey ? update(shape) : shape)) };
}

/**
 * Fields without the ones of this shape.
 * @param {object} fields - Fields.
 * @param {string} shapeKey - Shape key.
 * @returns {object} Fields.
 */
function withoutShapeFields(fields, shapeKey) {
  return Object.fromEntries(Object.entries(fields || {}).filter(([, field]) => field?.shapeKey !== shapeKey));
}

/**
 * Change what a shape does. Choosing an AI action gives it a field (the previous one when given,
 * so switching back keeps the prompt); any other action removes its field.
 * @param {object} model - Deck template.
 * @param {string} shapeKey - Shape key.
 * @param {string} action - New action.
 * @param {object} [options] - Options.
 * @param {string} [options.variable] - Code variable for 'variable'.
 * @param {object} [options.previousField] - Field (with `id`) this shape had before.
 * @returns {object} New model.
 */
export function setShapeAction(model, shapeKey, action, { variable, previousField } = {}) {
  if (!SHAPE_ACTIONS.some((a) => a.value === action)) return model;
  const shape = model.shapes.find((s) => s && s.key === shapeKey);
  if (!shape) return model;
  const current = fieldsByShape(model).get(shapeKey);
  let fieldId = null;
  let fields = model.fields;
  if (FIELD_ACTIONS.includes(action)) {
    if (current) {
      // Moving between AI field and optional only changes whether it is required.
      fieldId = current.id;
      fields = { ...fields, [fieldId]: { ...fields[fieldId], required: action === 'ai_field' } };
    } else {
      const restored = previousField && previousField.shapeKey === shapeKey ? previousField : null;
      fieldId = restored?.id && (!fields[restored.id] || fields[restored.id].shapeKey === shapeKey)
        ? restored.id
        : deckFieldId(shapeKey, fields);
      const kind = restored?.kind || defaultKindFor(shape);
      const field = restored
        ? { ...restored, id: fieldId, slideKey: shape.slideKey, state: 'accepted', required: action === 'ai_field' ? restored.required !== false : false }
        : {
            id: fieldId,
            shapeKey,
            slideKey: shape.slideKey,
            kind,
            instruction: '',
            source: 'any',
            required: action === 'ai_field',
            limits: limitsFromCapacity(shape.capacity, kind),
            fit: defaultFitFor(kind, shape.phType),
            origin: 'user',
            state: 'accepted',
            confidence: null,
          };
      fields = { ...fields, [fieldId]: field };
    }
  } else if (current) {
    fields = withoutShapeFields(fields, shapeKey);
  }
  return mapShape({ ...model, fields }, shapeKey, (s) => ({
    ...s,
    action,
    variable: action === 'variable' ? (variable || s.variable || CODE_VARIABLES[0].name) : null,
    fieldId,
  }));
}

/**
 * Set a code variable on a variable shape.
 * @param {object} model - Deck template.
 * @param {string} shapeKey - Shape key.
 * @param {string} variable - Variable name.
 * @returns {object} New model.
 */
export function setShapeVariable(model, shapeKey, variable) {
  if (!CODE_VARIABLES.some((v) => v.name === variable)) return model;
  return mapShape(model, shapeKey, (s) => ({ ...s, action: 'variable', variable }));
}

/**
 * Edit a field. Editing a suggestion accepts it. Limits merge, are capped at what the box holds,
 * and an empty limit falls back to the box capacity.
 * @param {object} model - Deck template.
 * @param {string} fieldId - Field id.
 * @param {object} patch - Changes.
 * @returns {object} New model.
 */
export function updateField(model, fieldId, patch) {
  const field = model.fields[fieldId];
  if (!field || !isObject(patch)) return model;
  const shape = model.shapes.find((s) => s && s.key === field.shapeKey);
  const capacity = capacityOf(shape);
  const next = { ...field };
  Object.entries(patch).forEach(([key, value]) => {
    if (key === 'limits') {
      const limits = { ...(isObject(field.limits) ? field.limits : {}) };
      Object.entries(value || {}).forEach(([limitKey, limitValue]) => {
        if (!LIMIT_KEYS.includes(limitKey)) return;
        const n = finite(limitValue);
        if (n === null || n <= 0) limits[limitKey] = capacity[limitKey] ?? null;
        else limits[limitKey] = Math.min(Math.floor(n), capacity[limitKey] ?? Infinity);
      });
      next.limits = limits;
    } else if (key === 'instruction') {
      next.instruction = String(value ?? '').slice(0, MAX_INSTRUCTION_LENGTH);
    } else if (key === 'kind') {
      if (FIELD_KINDS.some((k) => k.value === value) && value !== field.kind) {
        next.kind = value;
        // Limits that make sense for the new kind, within the box.
        next.limits = limitsFromCapacity(shape?.capacity, value);
      }
    } else if (key === 'source') {
      if (FIELD_SOURCES.some((s) => s.value === value)) next.source = value;
    } else if (key === 'fit') {
      if (FIT_POLICIES.some((f) => f.value === value)) next.fit = value;
    } else if (key === 'required') {
      next.required = Boolean(value);
    }
  });
  if (field.state !== 'accepted') next.state = 'accepted';
  // A rule or AI suggestion the owner changed is marked, so "accepted unchanged" can be told from
  // "edited then accepted" (the owner's review metric).
  if (field.origin !== 'user' && EDIT_KEYS.some((key) => JSON.stringify(next[key] ?? null) !== JSON.stringify(field[key] ?? null))) next.edited = true;
  return { ...model, fields: { ...model.fields, [fieldId]: next } };
}

/** Field properties whose change marks a suggestion as edited. */
const EDIT_KEYS = ['instruction', 'kind', 'source', 'fit', 'required', 'limits'];

/**
 * Accept suggested fields unchanged.
 * @param {object} model - Deck template.
 * @param {string[]} fieldIds - Field ids.
 * @returns {object} New model (the same object when nothing changes).
 */
export function acceptFields(model, fieldIds) {
  const ids = fieldIds.filter((id) => model.fields[id] && model.fields[id].state !== 'accepted');
  if (ids.length === 0) return model;
  const fields = { ...model.fields };
  ids.forEach((id) => {
    fields[id] = { ...fields[id], state: 'accepted' };
  });
  return { ...model, fields };
}

/**
 * Reject suggested fields: their shapes become fixed and the fields are removed.
 * @param {object} model - Deck template.
 * @param {string[]} fieldIds - Field ids.
 * @returns {object} New model (the same object when nothing changes).
 */
export function rejectFields(model, fieldIds) {
  const ids = fieldIds.filter((id) => model.fields[id]);
  if (ids.length === 0) return model;
  const shapeKeys = new Set(ids.map((id) => model.fields[id].shapeKey));
  const fields = Object.fromEntries(Object.entries(model.fields).filter(([id]) => !ids.includes(id)));
  const shapes = model.shapes.map((shape) => (
    shape && shapeKeys.has(shape.key) ? { ...shape, action: 'fixed', variable: null, fieldId: null } : shape
  ));
  return { ...model, shapes, fields };
}

/**
 * Accept every suggestion in the deck.
 * @param {object} model - Deck template.
 * @returns {object} New model.
 */
export function acceptAllSuggestions(model) {
  return acceptFields(model, suggestedFieldIds(model));
}

/**
 * Edit a slide (`repeat` and `notes` merge). Any include rule other than 'exclude' brings a
 * dropped slide back (its drop reason goes).
 * @param {object} model - Deck template.
 * @param {string} slideKey - Slide key.
 * @param {object} patch - Changes.
 * @returns {object} New model.
 */
export function updateSlide(model, slideKey, patch) {
  if (!isObject(patch)) return model;
  const restoring = typeof patch.include === 'string' && patch.include !== 'exclude' && INCLUDE_RULES.some((r) => r.value === patch.include);
  const next = {
    ...model,
    slides: model.slides.map((slide) => {
      if (!slide || slide.key !== slideKey) return slide;
      const updated = { ...slide };
      Object.entries(patch).forEach(([key, value]) => {
        if (key === 'repeat' || key === 'notes') {
          updated[key] = { ...(isObject(slide[key]) ? slide[key] : {}), ...(value || {}) };
        } else if (key === 'include') {
          if (INCLUDE_RULES.some((r) => r.value === value)) updated.include = value;
        } else if (key === 'role') {
          if (SLIDE_ROLES.includes(value)) updated.role = value;
        } else if (key === 'purpose') {
          updated.purpose = String(value ?? '').slice(0, MAX_PURPOSE_CHARS);
        } else if (key === 'excludeReason') {
          updated.excludeReason = String(value ?? '').slice(0, MAX_REASON_CHARS);
        } else if (key === 'excludeOrigin') {
          updated.excludeOrigin = value;
        }
      });
      if (restoring) {
        updated.excludeReason = '';
        updated.excludeOrigin = null;
      }
      if (updated.include === 'repeat') {
        const repeat = { min: 1, max: 10, unit: 'ai', ...(isObject(updated.repeat) ? updated.repeat : {}) };
        // Same rule as the normaliser (max is never below min), so what is shown is what is saved.
        const min = Number.isFinite(Number(repeat.min)) ? Math.max(0, Math.floor(Number(repeat.min))) : 1;
        const max = Number.isFinite(Number(repeat.max)) ? Math.max(1, Math.floor(Number(repeat.max))) : Math.max(1, min);
        updated.repeat = { ...repeat, min, max: Math.max(min, max) };
      }
      return updated;
    }),
  };
  if (restoring && Array.isArray(model.droppedSlides)) {
    next.droppedSlides = model.droppedSlides.filter((item) => (item?.slideKey || item?.key) !== slideKey);
  }
  return next;
}

/**
 * Leave a slide out of generated decks (kept, with the reason, so it can be restored).
 * @param {object} model - Deck template.
 * @param {string} slideKey - Slide key.
 * @param {string} [reason] - Why.
 * @returns {object} New model.
 */
export function dropSlide(model, slideKey, reason = 'Left out in the editor') {
  return updateSlide(model, slideKey, { include: 'exclude', excludeReason: reason, excludeOrigin: 'user' });
}

/**
 * Bring a dropped slide back into generated decks.
 * @param {object} model - Deck template.
 * @param {string} slideKey - Slide key.
 * @param {string} [include] - Include rule to use.
 * @returns {object} New model.
 */
export function restoreSlide(model, slideKey, include = 'always') {
  return updateSlide(model, slideKey, { include: include === 'exclude' ? 'always' : include });
}

/**
 * Edit the deck brief.
 * @param {object} model - Deck template.
 * @param {object} patch - `{audience?, purpose?, narrative?}`.
 * @returns {object} New model.
 */
export function updateBrief(model, patch) {
  const brief = { ...(isObject(model.brief) ? model.brief : {}) };
  ['audience', 'purpose', 'narrative'].forEach((key) => {
    if (patch && key in patch) brief[key] = String(patch[key] ?? '').slice(0, MAX_BRIEF_PART_CHARS);
  });
  return { ...model, brief };
}

/**
 * Theme colours and fonts for the deck tab (read only in this wave).
 * @param {object} model - Deck template.
 * @returns {{colors: Array<{name: string, value: string}>, fonts: Array<{name: string, value: string}>}} Tokens.
 */
export function themeTokensOf(model) {
  const theme = isObject(model?.theme) ? model.theme : isObject(model?.themeTokens) ? model.themeTokens : {};
  const colors = Object.entries(isObject(theme.colors) ? theme.colors : {})
    .map(([name, value]) => ({ name, value: typeof value === 'string' ? value : value?.hex || value?.value || '' }))
    .map((c) => ({ ...c, value: /^#?[0-9a-f]{6}$/i.test(c.value) ? (c.value.startsWith('#') ? c.value : `#${c.value}`) : '' }))
    .filter((c) => c.value);
  const fonts = Object.entries(isObject(theme.fonts) ? theme.fonts : {})
    .map(([name, value]) => ({ name, value: typeof value === 'string' ? value : value?.latin || value?.face || '' }))
    .filter((f) => f.value);
  return { colors, fonts };
}
